import React, { useEffect, useCallback, useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowRight, Shield, Building2, FileText, Landmark, Scale, CheckCircle, ChevronRight, Heart } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { publicApi } from '../lib/api';
import { useLanguage } from '../hooks/useLanguage';
import { useCMSContent } from '../hooks/useCMSContent';
import SEO from '../components/SEO';

const ServiceDetail = ({ slug }) => {
  const { t } = useLanguage();
  
  // Slugs that have their own dedicated pages - redirect users there
  const dedicatedPages = {
    'government-contracts': '/government-contracts',
    'family-legal-planning': '/family-legal-planning',
  };
  
  const shouldRedirect = dedicatedPages[slug];
  
  // Map slug to translation key
  const slugToKey = {
    'trademark': 'trademarks',
    'business-formation': 'businessFormation',
    'contracts': 'contracts',
    'personal-injury': 'personalInjury',
  };
  
  const serviceKey = slugToKey[slug];
  const icons = {
    'trademark': Shield,
    'business-formation': Building2,
    'contracts': FileText,
    'personal-injury': Scale,
  };

  useEffect(() => {
    // Handle redirect in useEffect to avoid breaking hooks rules
    if (shouldRedirect) {
      window.location.href = dedicatedPages[slug];
      return;
    }
    publicApi.trackEvent({ event_type: 'page_view', page: `service_${slug}` }).catch(() => {});
  }, [slug, shouldRedirect]);

  // Show loading state while redirecting
  if (shouldRedirect) {
    return (
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 text-center">
        <p className="text-[#555555]">Redirecting...</p>
      </div>
    );
  }

  if (!serviceKey) {
    return (
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 text-center">
        <h1 className="text-3xl font-serif text-[#222222] mb-4">{t('services.serviceNotFound')}</h1>
        <Link to="/services" className="text-[#1F2A44] underline">{t('services.backToServices')}</Link>
      </div>
    );
  }

  const Icon = icons[slug];
  const serviceTitle = t(`services.${serviceKey}.title`);
  const serviceSubtitle = t(`services.${serviceKey}.subtitle`);
  const serviceDescription = t(`services.${serviceKey}.description`);
  const serviceDifference = t(`services.${serviceKey}.difference`);
  
  // Get arrays from translations (these are stored as arrays in the JSON)
  const whoItsForItems = t(`services.${serviceKey}.whoItsFor`);
  const whatYouGetItems = t(`services.${serviceKey}.whatYouGet`);
  const pricingItems = t(`services.${serviceKey}.pricingItems`);
  const faqItems = t(`services.${serviceKey}.faqs`);

  return (
    <div data-testid={`service-detail-${slug}`}>
      <SEO 
        title={serviceTitle}
        description={serviceDescription}
      />
      {/* Hero */}
      <section className="bg-[#FDFCFA] border-b border-[#E8E4DC]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-24">
          <div className="max-w-3xl">
            <div className="flex items-center gap-3 mb-6">
              <Icon className="w-8 h-8 text-[#1F2A44]" />
              <p className="text-xs font-mono uppercase tracking-widest text-[#C2A46D]">
                {t('services.practiceArea')}
              </p>
            </div>
            <h1 className="text-4xl sm:text-5xl font-serif tracking-tight leading-[1.1] text-[#222222] mb-4">
              {serviceTitle}
            </h1>
            <p className="text-xl text-[#1F2A44] font-serif mb-6">
              {serviceSubtitle}
            </p>
            <p className="text-lg text-[#555555] leading-relaxed mb-8">
              {serviceDescription}
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              {slug === 'trademark' && (
                <a href="https://app.practicepanther.com/IntakeForm/Create?templateGuid=practice-area-deploy" target="_blank" rel="noopener noreferrer" data-testid="service-cta-get-started">
                  <Button className="bg-[#C2A46D] hover:bg-[#A88B55] text-[#1F2A44] px-8 py-4 rounded-sm text-sm font-medium tracking-wide uppercase">
                    {t('services.getStarted') || 'Get Started'}
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </a>
              )}
              {slug === 'business-formation' && (
                <a href="https://app.practicepanther.com/IntakeForm/Create?templateGuid=practice-area-deploy" target="_blank" rel="noopener noreferrer" data-testid="service-cta-get-started">
                  <Button className="bg-[#C2A46D] hover:bg-[#A88B55] text-[#1F2A44] px-8 py-4 rounded-sm text-sm font-medium tracking-wide uppercase">
                    {t('services.getStarted') || 'Get Started'}
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </a>
              )}
              {slug === 'personal-injury' && (
                <a href="https://app.practicepanther.com/IntakeForm/Create?templateGuid=practice-area-deploy" target="_blank" rel="noopener noreferrer" data-testid="service-cta-get-started">
                  <Button className="bg-[#C2A46D] hover:bg-[#A88B55] text-[#1F2A44] px-8 py-4 rounded-sm text-sm font-medium tracking-wide uppercase">
                    {t('services.getStarted') || 'Get Started'}
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </a>
              )}
              {slug === 'contracts' && (
                <a href="https://app.practicepanther.com/IntakeForm/Create?templateGuid=practice-area-deploy" target="_blank" rel="noopener noreferrer" data-testid="service-cta-get-started">
                  <Button className="bg-[#C2A46D] hover:bg-[#A88B55] text-[#1F2A44] px-8 py-4 rounded-sm text-sm font-medium tracking-wide uppercase">
                    {t('services.getStarted') || 'Get Started'}
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </a>
              )}
              <Link to="/book" data-testid="service-cta-book">
                <Button className="bg-[#1F2A44] hover:bg-[#151D30] text-white px-8 py-4 rounded-sm text-sm font-medium tracking-wide uppercase">
                  {t('services.bookConsultation')}
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Who It's For */}
      <section className="bg-white border-b border-[#E8E4DC]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-20">
          <h2 className="text-2xl md:text-3xl font-serif text-[#222222] mb-8">{t('services.whoItsFor')}</h2>
          <ul className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {Array.isArray(whoItsForItems) && whoItsForItems.map((item, index) => (
              <li key={index} className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-[#1F2A44] mt-0.5 flex-shrink-0" />
                <span className="text-[#555555]">{item}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* What You Get */}
      <section className="bg-[#F5F3EF] border-b border-[#E8E4DC]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-20">
          <h2 className="text-2xl md:text-3xl font-serif text-[#222222] mb-8">{t('services.whatYouGet')}</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {Array.isArray(whatYouGetItems) && whatYouGetItems.map((item, index) => (
              <div key={index} className="bg-white p-6 border border-[#E8E4DC]">
                <div className="text-xs font-mono text-[#C2A46D] mb-2">0{index + 1}</div>
                <p className="text-[#222222]">{item}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What Makes This Different */}
      <section className="bg-[#1F2A44] text-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-20">
          <h2 className="text-2xl md:text-3xl font-serif mb-6">{t('services.whatMakesDifferent')}</h2>
          <p className="text-lg text-white/80 leading-relaxed max-w-3xl">
            {serviceDifference}
          </p>
        </div>
      </section>

      {/* Pricing */}
      <section className="bg-white border-b border-[#E8E4DC]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-20">
          <h2 className="text-2xl md:text-3xl font-serif text-[#222222] mb-8">{t('services.pricing')}</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {Array.isArray(pricingItems) && pricingItems.map((item, index) => (
              <div key={index} className="p-6 border border-[#E8E4DC] bg-[#FDFCFA]">
                <p className="text-[#222222] font-medium mb-2">{item.name}</p>
                <p className="text-xl font-serif text-[#1F2A44]">{item.price}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section className="bg-[#FDFCFA] border-b border-[#E8E4DC]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-20">
          <h2 className="text-2xl md:text-3xl font-serif text-[#222222] mb-8">{t('services.faq')}</h2>
          <Accordion type="single" collapsible className="max-w-3xl">
            {Array.isArray(faqItems) && faqItems.map((faq, index) => (
              <AccordionItem key={index} value={`faq-${index}`} className="border-b border-[#E8E4DC]">
                <AccordionTrigger className="text-left text-[#222222] hover:text-[#1F2A44] py-4">
                  {faq.q}
                </AccordionTrigger>
                <AccordionContent className="text-[#555555] pb-4">
                  {faq.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#1F2A44]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-20 text-center">
          <h2 className="text-3xl md:text-4xl font-serif text-white mb-6">
            {t('cta.headline')}
          </h2>
          <p className="text-lg text-white/80 mb-8 max-w-2xl mx-auto">
            {t('cta.subheadline')}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            {slug === 'trademark' && (
              <a href="https://app.practicepanther.com/IntakeForm/Create?templateGuid=practice-area-deploy" target="_blank" rel="noopener noreferrer">
                <Button className="bg-[#C2A46D] hover:bg-[#A88B55] text-[#1F2A44] px-10 py-4 rounded-sm text-sm font-medium tracking-wide uppercase">
                  {t('services.getStarted') || 'Get Started'}
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </a>
            )}
            {slug === 'business-formation' && (
              <a href="https://app.practicepanther.com/IntakeForm/Create?templateGuid=practice-area-deploy" target="_blank" rel="noopener noreferrer">
                <Button className="bg-[#C2A46D] hover:bg-[#A88B55] text-[#1F2A44] px-10 py-4 rounded-sm text-sm font-medium tracking-wide uppercase">
                  {t('services.getStarted') || 'Get Started'}
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </a>
            )}
            {slug === 'personal-injury' && (
              <a href="https://app.practicepanther.com/IntakeForm/Create?templateGuid=practice-area-deploy" target="_blank" rel="noopener noreferrer">
                <Button className="bg-[#C2A46D] hover:bg-[#A88B55] text-[#1F2A44] px-10 py-4 rounded-sm text-sm font-medium tracking-wide uppercase">
                  {t('services.getStarted') || 'Get Started'}
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </a>
            )}
            {slug === 'contracts' && (
              <a href="https://app.practicepanther.com/IntakeForm/Create?templateGuid=practice-area-deploy" target="_blank" rel="noopener noreferrer">
                <Button className="bg-[#C2A46D] hover:bg-[#A88B55] text-[#1F2A44] px-10 py-4 rounded-sm text-sm font-medium tracking-wide uppercase">
                  {t('services.getStarted') || 'Get Started'}
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </a>
            )}
            <Link to="/book">
              <Button className="bg-white text-[#1F2A44] hover:bg-[#FDFCFA] px-10 py-4 rounded-sm text-sm font-medium tracking-wide uppercase">
                {t('services.bookConsultation')}
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

const ServicesHub = () => {
  const { t, language } = useLanguage();
  const { blocks } = useCMSContent('services');

  // Helper to get CMS content with language support, falling back to translations
  const getCMSText = useCallback((blockKey, field, translationKey) => {
    const block = blocks[blockKey];
    if (block && Object.keys(block).length > 0) {
      const langField = language === 'es' ? `${field}_es` : field;
      return block[langField] || block[field] || t(translationKey);
    }
    return t(translationKey);
  }, [blocks, language, t]);
  
  useEffect(() => {
    publicApi.trackEvent({ event_type: 'page_view', page: 'services' }).catch(() => {});
  }, []);

  const servicesList = [
    {
      slug: 'trademark',
      link: '/services/trademark',
      icon: Shield,
      title: t('services.trademarks.title'),
      description: t('services.trademarks.description'),
    },
    {
      slug: 'business-formation',
      link: '/services/business-formation',
      icon: Building2,
      title: t('services.businessFormation.title'),
      description: t('services.businessFormation.description'),
    },
    {
      slug: 'contracts',
      link: '/services/contracts',
      icon: FileText,
      title: t('services.contracts.title'),
      description: t('services.contracts.description'),
    },
    {
      slug: 'government-contracts',
      link: '/government-contracts',
      icon: Landmark,
      title: t('services.governmentContracts.title'),
      description: t('services.governmentContracts.description'),
    },
    {
      slug: 'personal-injury',
      link: '/services/personal-injury',
      icon: Scale,
      title: t('services.personalInjury.title'),
      description: t('services.personalInjury.description'),
    },
    {
      slug: 'family-legal-planning',
      link: '/family-legal-planning',
      icon: Heart,
      title: t('services.familyLegalPlanning.title'),
      description: t('services.familyLegalPlanning.description'),
    },
  ];

  return (
    <div data-testid="services-page">
      {/* Hero */}
      <section className="bg-[#FDFCFA] border-b border-[#E8E4DC]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-24">
          <div className="max-w-3xl">
            <p className="text-xs font-mono uppercase tracking-widest text-[#C2A46D] mb-6">
              {getCMSText('hero', 'badge', 'services.sectionLabel')}
            </p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-serif tracking-tight leading-[1.1] text-[#222222] mb-6">
              {getCMSText('hero', 'headline', 'services.headline')}
            </h1>
            <p className="text-lg text-[#555555] leading-relaxed">
              {getCMSText('hero', 'description', 'hero.subheadline')}
            </p>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-24">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {servicesList.map((service, index) => {
              const Icon = service.icon;
              return (
                <Link
                  key={service.slug}
                  to={service.link}
                  className="group relative bg-[#FDFCFA] p-8 md:p-10 border border-[#E8E4DC] hover:border-[#1F2A44]/30 hover:shadow-xl transition-all duration-500"
                  data-testid={`service-link-${service.slug}`}
                >
                  <Icon className="w-10 h-10 text-[#1F2A44] mb-6" />
                  <h2 className="text-2xl font-serif text-[#222222] mb-3 group-hover:text-[#1F2A44] transition-colors">
                    {service.title}
                  </h2>
                  <p className="text-[#555555] leading-relaxed mb-4">
                    {service.description}
                  </p>
                  <span className="inline-flex items-center text-sm font-medium text-[#1F2A44] group-hover:gap-2 transition-all">
                    {t('services.learnMore')}
                    <ChevronRight className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity" />
                  </span>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#1F2A44]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-20 text-center">
          <h2 className="text-3xl md:text-4xl font-serif text-white mb-6">
            {t('cta.headline')}
          </h2>
          <p className="text-lg text-white/80 mb-8 max-w-2xl mx-auto">
            {t('cta.subheadline')}
          </p>
          <Link to="/book">
            <Button className="bg-white text-[#1F2A44] hover:bg-[#FDFCFA] px-10 py-4 rounded-sm text-sm font-medium tracking-wide uppercase">
              {t('nav.bookConsultation')}
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

const ServicesPage = () => {
  const { slug } = useParams();
  
  if (slug) {
    return <ServiceDetail slug={slug} />;
  }
  
  return <ServicesHub />;
};

export default ServicesPage;
